import React, { Suspense, lazy } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import Layout from './Layout';
import LoadingSpinner from './components/common/LoadingSpinner';
import { CartProvider } from './contexts/CartContext';

// Lazy load pages
const Home = lazy(() => import('./pages/Home'));
const Marketplace = lazy(() => import('./pages/Marketplace'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Cart = lazy(() => import('./pages/Cart'));
const Checkout = lazy(() => import('./pages/Checkout'));
const BuyerOrders = lazy(() => import('./pages/BuyerOrders'));
const BuyerDashboard = lazy(() => import('./pages/BuyerDashboard'));
const FarmerRegistration = lazy(() => import('./pages/FarmerRegistration'));
const FarmerDashboard = lazy(() => import('./pages/FarmerDashboard'));
const CreateProduct = lazy(() => import('./pages/CreateProduct'));
const FarmerOrders = lazy(() => import('./pages/FarmerOrders'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const Login = lazy(() => import('./pages/Login'));
const Signup = lazy(() => import('./pages/Signup'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const GoogleCallback = lazy(() => import('./pages/GoogleCallback'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const Disclaimer = lazy(() => import('./pages/Disclaimer'));
const FAQ = lazy(() => import('./pages/FAQ'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const AboutUs = lazy(() => import('./pages/AboutUs'));
const ContactUs = lazy(() => import('./pages/ContactUs'));
const BuyerSubscriptions = lazy(() => import('./pages/BuyerSubscriptions'));
const FarmerSubscriptionPlans = lazy(() => import('./pages/FarmerSubscriptionPlans'));
const DeliveryTracking = lazy(() => import('./pages/DeliveryTracking'));
const AdminDeliveryManagement = lazy(() => import('./pages/AdminDeliveryManagement'));
const FarmerAnalytics = lazy(() => import('./pages/FarmerAnalytics'));
const SupportCenter = lazy(() => import('./pages/SupportCenter'));
const DriverDashboard = lazy(() => import('./pages/DriverDashboard'));
const DriverLogin = lazy(() => import('./pages/DriverLogin'));
const Messages = lazy(() => import('./pages/Messages'));
const ReferralPage = lazy(() => import('./pages/ReferralPage'));
const Wishlist = lazy(() => import('./pages/Wishlist'));
const DriverRegistration = lazy(() => import('./pages/DriverRegistration'));

const AdminMarketingAgents = lazy(() => import('./pages/AdminMarketingAgents'));
const OAuthVerify = lazy(() => import('./pages/OAuthVerify'));
const DomainRequest = lazy(() => import('./pages/DomainRequest'));
const DomainRegister = lazy(() => import('./pages/DomainRegister'));

export default function App() {
  const location = useLocation();

  const getPageName = () => {
    const path = location.pathname;
    if (path === '/') return 'Home';
    return path.slice(1).split('/')[0];
  };

  const currentPageName = getPageName();

  return (
    <CartProvider>
      <Layout currentPageName={currentPageName} pageData={window.currentPageData || {}}>
        <Suspense fallback={
          <div className="flex justify-center items-center min-h-screen">
            <LoadingSpinner />
          </div>
        }>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/auth/google/callback" element={<GoogleCallback />} />
            <Route path="/oauth/verify" element={<OAuthVerify />} />
            <Route path="/marketplace" element={<Marketplace />} />
            <Route path="/product/:id" element={<ProductDetail />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/dashboard" element={<BuyerDashboard />} />
            <Route path="/orders" element={<BuyerOrders />} />
            <Route path="/messages" element={<Messages />} />
            <Route path="/delivery/track" element={<DeliveryTracking />} />
            <Route path="/delivery/:orderId" element={<DeliveryTracking />} />
            <Route path="/referral" element={<ReferralPage />} />
            <Route path="/wishlist" element={<Wishlist />} />
            <Route path="/subscriptions" element={<BuyerSubscriptions />} />
            <Route path="/farmer/register" element={<FarmerRegistration />} />
            <Route path="/farmer/dashboard" element={<FarmerDashboard />} />
            <Route path="/farmer/orders" element={<FarmerOrders />} />
            <Route path="/farmer/subscriptions" element={<FarmerSubscriptionPlans />} />
            <Route path="/farmer/product/new" element={<CreateProduct />} />
            <Route path="/farmer/product/:id" element={<CreateProduct />} />
            <Route path="/farmer/analytics" element={<FarmerAnalytics />} />

            {/* Admin Dashboard */}
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/delivery-management" element={<AdminDeliveryManagement />} />
            <Route path="/admin/marketing-agents" element={<AdminMarketingAgents />} />

            {/* Driver Dashboard */}
            <Route path="/driver/login" element={<DriverLogin />} />
            <Route path="/driver/register" element={<DriverRegistration />} />
            <Route path="/driver/dashboard" element={<DriverDashboard />} />

            <Route path="/support" element={<SupportCenter />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/cookies" element={<CookiePolicy />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/domain-request" element={<DomainRequest />} />
            <Route path="/domain-register" element={<DomainRegister />} />
          </Routes>
        </Suspense>
      </Layout>
    </CartProvider>
  );
}

